/* eslint-disable perfectionist/sort-object-types */
import { create } from 'zustand';

import type {
	JobChangeCount,
	EducationLevel,
	EmploymentStatus,
	Gender,
	GraduationStatus,
} from '@packages/contracts';

export type PersonalInfo = {
	provinceId:  string
	region:      string
	dateOfBirth: string
	email:       string
	firstName:   string
	lastName:    string
	gender:      Gender
	phoneNumber: string
};

export type EducationBackground = {
	educationLevel:   EducationLevel
	graduationStatus: GraduationStatus
	graduationDate?:  string
};

export type WorkHistory = {
	companyName:      string
	hideCompanyName:  boolean
	employmentStatus: EmploymentStatus
	occupationId:     string
	industryId:       string
	jobChangeCount:   JobChangeCount
};

export type JobPreference = {
	currentAnnualSalary:    string
	expectedAnnualSalary:   string
	preferredOccupationIds: string[]
	preferredLocationIds:   string[]
	openToAnyLocations:     boolean
};

export type RegistrationModel = {
	personalInfo:  PersonalInfo
	education:     EducationBackground
	workHistory:   WorkHistory
	jobPreference: JobPreference
};

type RegistrationState = {
	registration:     RegistrationModel
	getPersonalInfo:  () => PersonalInfo
	getEducation:     () => EducationBackground
	getWorkHistory:   () => WorkHistory
	getJobPreference: () => JobPreference
	setPersonalInfo:  (personalInfo: Partial<PersonalInfo>) => void
	setEducation:     (education: Partial<EducationBackground>) => void
	setWorkHistory:   (workHistory: Partial<WorkHistory>) => void
	setJobPreference: (jobPreference: Partial<JobPreference>) => void
};

export const useRegistrationStore = create<RegistrationState>((set, get) => ({
	registration: {} as never,

	getEducation:     () => get().registration.education,
	getJobPreference: () => get().registration.jobPreference,
	getPersonalInfo:  () => get().registration.personalInfo,
	getWorkHistory:   () => get().registration.workHistory,
	setEducation:     (education: Partial<EducationBackground>) => {
		set(state => ({
			registration: {
				...state.registration,
				education: {
					...state.registration?.education,
					...education,
				},
			},
		}));
	},
	setJobPreference: (jobPreference: Partial<JobPreference>) => {
		set(state => ({
			registration: {
				...state.registration,
				jobPreference: {
					...state.registration?.jobPreference,
					...jobPreference,
				},
			},
		}));
	},
	setPersonalInfo: (personalInfo: Partial<PersonalInfo>) => {
		set(state => ({
			registration: {
				...state.registration,
				personalInfo: {
					...state.registration?.personalInfo,
					...personalInfo,
				},
			},
		}));
	},
	setWorkHistory: (workHistory: Partial<WorkHistory>) => {
		set(state => ({
			registration: {
				...state.registration,
				workHistory: {
					...state.registration?.workHistory,
					...workHistory,
				},
			},
		}));
	},
}));
